/**
 * The offline-attendance orchestrator (`specs/offline-attendance-v1/implementation-plan.md`,
 * task FE-9; decisions D3, D6, D7, D9, D11, D13, D14; amendments C10–C15 and
 * C18–C21; Review Focus 1–5).
 *
 * ONE module wires the pieces together for the signed-in session:
 *
 *   start ─▶ data key + installation id + scope ─▶ the device's own context,
 *            snapshot and chain ─▶ readiness + projection (published at once,
 *            WITHOUT waiting for the network) ─▶ the sync engine ─▶ wakes ─▶
 *            provisioning in the background.
 *
 * Load-bearing rules:
 *   - **One clock monitor per JS process** (C11): a module singleton that
 *     outlives sign-out and sign-in, told about visibility (and the native
 *     pause) for its whole life. Every session start restores it from the
 *     clock state persisted for this API origin and from the stored context's
 *     calibration; every successful provisioning gives it the new policy's
 *     thresholds and THEN the new calibration, with no unrelated `await` in
 *     between; every capture is stamped by it (C10). Its state is persisted in
 *     `meta` under `clock_state:<apiOrigin>` — never device-wide: a calibration
 *     measured against one ERPat must never stamp punches for another.
 *   - **Nothing outlives its session** (Review Focus 3): every async step
 *     re-checks the session epoch and this orchestrator's generation after
 *     each `await`; `stopOfflineAttendance()` is synchronous, stops the engine,
 *     aborts what is in flight, detaches every wake and resets the stores — and
 *     never touches IndexedDB data: the same account signing back in resumes
 *     its queue, another account never sees it.
 *   - **Capture is serialized and re-validated** (C13, Review Focus 2): inside
 *     a cross-tab Web Lock (when the browser has one) the chain and the
 *     snapshot are re-read, re-projected and readiness recomputed from ONE
 *     stamp before anything is decided; the commit's head/tip check catches a
 *     tab without Web Locks.
 *   - **Capture mode (D9)**: decided on the COMPUTED readiness — never on the
 *     "being set up again" display shown while the database is re-resolved:
 *     ready / degraded ⇒ the device queue first; online_only / preparing
 *     (which imply an EMPTY queue) ⇒ the online POST; anything else ⇒ refused
 *     with the reason's copy. A capture that meets a re-resolution (before the
 *     lock, or while re-reading the chain inside it) waits for it and decides
 *     again. There is never a legacy POST while queued history exists.
 *   - **A wiped database** (`onOfflineDbWiped`, or a capture refused as
 *     `wiped`, or a provisioning answered `stale/database`) stops capture and
 *     re-resolves the data key, the installation id and the scope TOGETHER —
 *     never the key alone — then rebuilds the engine and provisions again.
 *   - **Queued punches are always uploaded**, even when this build or platform
 *     does not capture (D6); provisioning (a grant request) runs only when it
 *     does. The start gate (no account, no IndexedDB, or no capture with
 *     nothing queued ⇒ load nothing, open nothing) applies at boot AND before
 *     every re-resolution.
 *   - **A failing store never refuses the online POST where no punch can be
 *     waiting** (D9 forbids it only while queued history may exist): every
 *     resolution first notes whether the database existed at all. When it did
 *     not, nothing was ever provisioned or queued on this device — the scope's
 *     context reads as absent, the queue as empty, and readiness keeps ERPat's
 *     own POST (online_only/storage_unavailable) unless ERPat says capture is
 *     on. Otherwise a device-wide count of zero empties the queue, and ERPat
 *     saying capture is off — even in an answer the store then failed to save —
 *     keeps the POST. ERPat saying it is ON keeps the storage block.
 *   - **Provisioning carries the stored pin** (D3): it runs only once the
 *     stored context was read (a failed read waits for storage to recover —
 *     unless the database did not exist before, when there is no pin to keep),
 *     and a refusal for a changed pin (`issuer_mismatch` / `employee_mismatch`)
 *     discards ONLY the stored context record — and only while the scope has
 *     nothing waiting; with punches waiting readiness is blocked/scope_mismatch
 *     and they keep uploading (C19).
 *   - **After an online punch** no queue-first capture builds on a confirmed
 *     snapshot read before it: the capture asks ERPat inside the lock and is
 *     refused `busy` until a snapshot read after the punch is stored (C20).
 *   - **ERPat's answers set the re-ask cadence**: a tenant that is not ready
 *     reads unsupported and is asked again every `OFFLINE_CONTEXT_REFRESH_MS`
 *     (C21) — a sync/state answer that disagrees does not hurry it; the review
 *     badge counts refusals settled within the last 7 days (C18).
 *
 * Exports: {@link startOfflineAttendance}, {@link stopOfflineAttendance},
 * {@link kickOfflineSync}, {@link offlineQuiesce}, {@link deviceUnsettledCount},
 * and {@link captureAttendance} (the store's `capture()` calls it).
 */
import { watch } from 'vue';
import { getActivePinia } from 'pinia';
import { Capacitor } from '@capacitor/core';
import { ErpatApiError } from '@erpat/sdk';
import {
  OFFLINE_ACCEPTED_PRUNE_MS,
  OFFLINE_CAPTURE_MIN_GAP_MS,
  OFFLINE_CLOCK_JUMP_DEFAULT_MS,
  OFFLINE_CLOCK_UNCERTAINTY_DEFAULT_MS,
  OFFLINE_CONTEXT_REFRESH_MS,
  OFFLINE_SYNC_BACKOFF_BASE_MS,
  OFFLINE_SYNC_PAUSE_RECHECK_MS,
  OFFLINE_SYNC_PERIODIC_MS,
  syncBackoffMs,
} from '@/contracts/offline-attendance-policy';
import type { AttendanceEventType, CapturedConnectivity, OfflinePolicyV1, SyncRequestV1 } from '@/contracts/offline-attendance-v1';
import { getSessionEpoch } from '@/services/api/erpat-client';
import { errorMessage, isForeignResponse, isSessionCancelled } from '@/services/api/http';
import { postSync } from '@/services/api/offline-attendance.api';
import { isOffline } from '@/services/connectivity';
import { appState } from '@/services/device/app-state.service';
import { attendanceService } from '@/services/erpat';
import {
  loadLocal,
  provision,
  refreshState,
  unsavedCapability,
  type ContextIntegrity,
  type ProvisionResult,
  type StateResult,
} from '@/services/offline/attendance-context.service';
import { MAX_BREAK_ENTRIES_DEFAULT, project } from '@/services/offline/attendance-projection';
import { ClockMonitor, monotonicClockAvailable, type ClockStamp } from '@/services/offline/clock-health';
import { resolveScope, sameOfflineScope, tokenIdentity } from '@/services/offline/offline-scope';
import { computeReadiness } from '@/services/offline/readiness';
import {
  countUnsettled,
  discardContextIfQueueEmpty,
  listChain,
  loadSnapshot,
  oldestUnsettledCreatedAt,
  pruneSettled,
} from '@/services/storage/attendance-storage';
import {
  OFFLINE_DB_NAME,
  isIndexedDbAvailable,
  offlineDb,
  onOfflineDbWiped,
  probeStorageHealth,
  type StorageHealth,
} from '@/services/storage/db';
import { loadDataKey, type DataKeyReady, type DataKeyState } from '@/services/storage/offline-keys';
import { SealedDataError } from '@/services/storage/sealed';
import { captureEvent, type OutboxCaptureResult } from '@/services/sync/outbox.service';
import { SyncEngine, type SyncPhaseReport } from '@/services/sync/sync-engine';
import { captureRefusalCopy } from '@/i18n/offline-attendance.en';
import { useAttendanceStore } from '@/stores/attendance.store';
import { useSyncStore } from '@/stores/sync.store';
import type {
  AttendanceProjection,
  CaptureInput,
  CaptureOutcome,
  ChainEvent,
  ClockCalibration,
  ConfirmedSnapshotRecord,
  OfflineContextRecord,
  OfflineReadiness,
  OfflineScope,
} from '@/types/offline-attendance';
import {
  API_BASE_URL,
  APP_VERSION,
  IS_DEV,
  OFFLINE_ATTENDANCE_ENABLED,
  OFFLINE_ATTENDANCE_NATIVE_ENABLED,
} from '@/utils/env';
import { localDateKey } from '@/utils/format';
import { newUuidV4 } from '@/utils/uuid';

/* ── Constants ─────────────────────────────────────────────────────────── */

/** `meta` key prefix of the persisted clock state (C11): `clock_state:<apiOrigin>`. */
const CLOCK_STATE_PREFIX = 'clock_state:';
/** The storage probe and the key load are bounded: a blocked open must never leave readiness preparing forever. */
const STORAGE_PROBE_TIMEOUT_MS = 15_000;
const KEY_LOAD_TIMEOUT_MS = 15_000;
/** A settlement window that no phase report closes ends on its own after this long (quiesce is bounded anyway). */
const SETTLING_MAX_MS = 10_000;
/** Two provisionings that are not urgent are at least this far apart. */
const PROVISION_MIN_SPACING_MS = OFFLINE_SYNC_BACKOFF_BASE_MS;
/**
 * C20: how long a queue-first capture made after an online punch waits, inside
 * the capture lock, for ERPat to confirm the base that punch moved. Past it the
 * capture is refused `busy` (the refresh goes on and lifts the hold when it lands).
 */
const ONLINE_CONFIRM_WAIT_MS = 8_000;

const PROBE_TIMED_OUT: StorageHealth = { status: 'failed', persisted: null, quota: null, usage: null, error: 'TimeoutError' };
const KEY_TIMED_OUT: DataKeyState = { status: 'unavailable', reason: 'error' };

type RefusalReason = Extract<CaptureOutcome, { kind: 'refused' }>['reason'];
type KeyInput = 'unknown' | 'ready' | 'lost' | 'unavailable';
type BackendInput = 'unknown' | 'unsupported' | 'disabled' | 'forbidden' | 'enabled';

/* ── Session state ─────────────────────────────────────────────────────── */

interface OfflineSession {
  /** The orchestrator generation the session was started under. */
  readonly gen: number;
  /** The auth session epoch it belongs to. */
  readonly epoch: number;
  started: Promise<void>;
  /** Everything a session start loads has been loaded (until then nothing is published). */
  loaded: boolean;
  /** This build, platform and browser may capture offline at all. */
  captureAllowed: boolean;
  environmentSupported: boolean;
  scope: OfflineScope | null;
  /**
   * The scope could not be resolved because IndexedDB failed (reading or
   * minting the installation id). With an identity, readiness then reports the
   * storage problem — never a sign-out.
   */
  scopeFailed: boolean;
  key: DataKeyReady | null;
  keyState: KeyInput;
  /** A stored row is sealed under another key: the chain cannot be read with this one. */
  chainKeyMismatch: boolean;
  storage: 'unknown' | 'ready' | 'failed';
  storagePersisted: boolean | null;
  /**
   * The offline database did not exist when the current resolution began —
   * listed by `offlineDbMayExist()` BEFORE anything below opens, and so
   * creates, it; `false` whenever that cannot be told. Nothing was ever
   * provisioned or queued on this device: when the store then fails, the
   * scope's context is known to be absent without reading it (provisioning may
   * ask fresh, D3), the queue counts as empty, and readiness keeps ERPat's own
   * POST (its 7–8 exception) unless ERPat says capture is on. Measured again by
   * every resolution — never carried over: once this session has written to the
   * database, a punch it queued may be waiting there.
   *
   * ⚠ Known narrow race, accepted: it is measured once, so ANOTHER tab that
   * creates the database and queues a punch after this reading — while this
   * tab's own store fails — is not seen, and this tab may clock online ahead of
   * that queued punch. It needs a store that fails in one tab and works in
   * another of the same origin; the other tab still uploads its punch, ERPat
   * rules on it against the attendance as it then stands, and a refusal shows
   * under Sync issues. The next resolution (a re-check, a restart) measures
   * again.
   */
  noPriorData: boolean;
  /**
   * The start gate (D6) found nothing to load: no account to scope by, no
   * IndexedDB, or a session that does not capture with nothing queued on the
   * device. Wakes and ticks then do nothing — no key read, no database opened.
   */
  dormant: boolean;
  /** What ERPat said this session (`unknown` until it answers). */
  backend: BackendInput;
  /**
   * ERPat's latest provisioning answer — saved or, when the store failed, not —
   * was `{false, 'not_ready'}` (C21). Only that answer is re-asked slowly; a
   * route or module that was missing is not.
   */
  notReadyAnswer: boolean;
  context: OfflineContextRecord | null;
  /**
   * The scope's stored context was READ (`loadLocal()` answered) for the
   * current key and scope — or is known to be absent without the read, the
   * database not having existed before this resolution (`noPriorData`).
   * Until then nothing is provisioned: a provisioning without the stored record
   * would pin the issuer anew and skip the employee-id continuity check (D3).
   */
  contextLoaded: boolean;
  integrity: ContextIntegrity;
  /**
   * The scope whose provisioning ERPat refused because the stored pin no
   * longer matches (`issuer_mismatch` / `employee_mismatch`, plan amendment
   * C19), else `null`. That scope's stored context is then not trusted for
   * capture: readiness reads blocked/scope_mismatch while punches wait (they
   * keep uploading), and once the queue is empty only the stored context
   * record is discarded and a fresh provisioning pins again. Kept across a
   * re-resolution of the SAME scope (it reloads the same pin), ignored for
   * another one (see {@link pinFailed}).
   */
  pinFailedScopeKey: string | null;
  /** A pin-failure recovery runs (single flight); asked again meanwhile ⇒ it looks once more. */
  pinRecovery: boolean;
  pinRecoveryAgain: boolean;
  /**
   * C20: online punches ERPat confirmed this session, and the newest of that
   * count a confirmed refresh (or provisioning) was STARTED after. While the
   * second is behind the first, the confirmed base may predate an online punch
   * and nothing is queued on it.
   */
  onlinePosts: number;
  confirmedAfterPosts: number;
  snapshot: ConfirmedSnapshotRecord | null;
  /** The scope's chain as last read; `null` when nothing could be read. */
  chain: ChainEvent[] | null;
  /**
   * Unsettled punches of the scope; `NaN` while unknown (readiness then assumes
   * some) — or `0` where none can be waiting although the store could not
   * count them ({@link countWhatMayWait}).
   */
  unsettled: number;
  oldestPendingAt: number | null;
  engine: SyncEngine | null;
  /** When uploads started failing (device ms), for readiness's sync_failing warning. */
  failingSinceDeviceMs: number | null;
  provisioning: Promise<void> | null;
  /** Identifies the provisioning in flight; bumped to orphan it (a re-resolution). */
  provisionRun: number;
  provisionAbort: AbortController | null;
  provisionTimer: ReturnType<typeof setTimeout> | null;
  provisionAttempt: number;
  lastProvisionAt: number | null;
  lastOnlineCaptureAt: number | null;
  /** Read sequence: a slower, older read of the chain never replaces a newer one. */
  readSeq: number;
  appliedReadSeq: number;
  localRefresh: Promise<void> | null;
  localRerun: boolean;
  resolving: Promise<void> | null;
  resolveAgain: boolean;
  /** Capture is suspended while the database is re-resolved. */
  suspended: boolean;
  wakesAttached: boolean;
  storageCheck: boolean;
  keyRetry: boolean;
  /** Aborts every request this session made (sign-out). */
  readonly abort: AbortController;
  readonly detach: Array<() => void>;
}

/** The single session; `null` when signed out. */
let session: OfflineSession | null = null;
/** Bumped by every stop: work started under an older generation changes nothing. */
let generation = 0;
/** Capture runs in progress (for {@link offlineQuiesce}). */
const busyWork = new Set<Promise<unknown>>();
/** An upload answered and is being settled (for {@link offlineQuiesce}). */
let settling: { promise: Promise<void>; resolve: () => void; timer: ReturnType<typeof setTimeout> } | null = null;

/* ── The clock monitor: one per JS process (C11) ───────────────────────── */

let monitor: ClockMonitor | null = null;
const monitorDetach: Array<() => void> = [];
/** The native shell reported the app paused (the WebView may still say visible). */
let appPaused = false;

/** THE clock monitor, created on first use with the default thresholds and visibility hooks for life. */
function clockMonitor(): ClockMonitor {
  if (monitor !== null) return monitor;
  const created = new ClockMonitor({
    jumpThresholdMs: OFFLINE_CLOCK_JUMP_DEFAULT_MS,
    uncertaintyThresholdMs: OFFLINE_CLOCK_UNCERTAINTY_DEFAULT_MS,
    isHidden: () => appPaused || documentHidden(),
  });
  monitor = created;
  attachMonitorHooks(created);
  return created;
}

/**
 * Tell the monitor about every hidden / visible change for its whole life —
 * not only while a session runs: `restore()` judges the interval since the
 * last observation, and an untold sleep would read as a forward jump.
 */
function attachMonitorHooks(m: ClockMonitor): void {
  if (typeof document !== 'undefined') {
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') m.markHidden();
      else m.markVisible();
    };
    document.addEventListener('visibilitychange', onVisibility);
    monitorDetach.push(() => document.removeEventListener('visibilitychange', onVisibility));
  }
  if (typeof window !== 'undefined') {
    const onPageHide = (): void => m.markHidden();
    const onPageShow = (): void => {
      if (!documentHidden()) m.markVisible();
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    monitorDetach.push(() => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    });
  }
  // The native pause / resume (Capacitor). Race-safe: detached before the plugin answered ⇒ detach then.
  let attached = true;
  let off: (() => void) | null = null;
  void appState
    .onAppStateChange((active) => {
      appPaused = !active;
      if (active) m.markVisible();
      else m.markHidden();
    })
    .then((detach) => {
      if (attached) off = detach;
      else detach();
    })
    .catch(() => undefined); // best effort: no native plugin ⇒ the web visibility hooks above suffice
  monitorDetach.push(() => {
    attached = false;
    off?.();
    off = null;
  });
}

/** The clock thresholds when no stored policy names its own (C11). */
const DEFAULT_THRESHOLDS = Object.freeze({
  jumpThresholdMs: OFFLINE_CLOCK_JUMP_DEFAULT_MS,
  uncertaintyThresholdMs: OFFLINE_CLOCK_UNCERTAINTY_DEFAULT_MS,
});

function thresholdsOf(policy: OfflinePolicyV1): { jumpThresholdMs: number; uncertaintyThresholdMs: number } {
  return {
    jumpThresholdMs: policy.clock_jump_block_s * 1_000,
    uncertaintyThresholdMs: policy.clock_uncertainty_block_s * 1_000,
  };
}

/* ── Public API ────────────────────────────────────────────────────────── */

/**
 * Start offline attendance for the signed-in session. Idempotent per session
 * (the same promise while the session epoch stays the same). Resolves once the
 * device's own state is loaded and published — it never waits for the
 * network: provisioning runs in the background. Never rejects.
 */
export function startOfflineAttendance(): Promise<void> {
  const epoch = getSessionEpoch();
  const current = session;
  if (current !== null && current.epoch === epoch && current.gen === generation) return current.started;
  if (current !== null) stopOfflineAttendance();

  const s = newSession(generation, epoch);
  session = s;
  s.started = boot(s).catch((err: unknown) => {
    // A bug, not an outcome: the session stays up and the next wake tries again.
    if (IS_DEV) console.warn('[offline-attendance] start failed', errorName(err));
  });
  return s.started;
}

/**
 * Stop offline attendance (sign-out, expiry, company switch). SYNCHRONOUS:
 * bumps the generation, stops the sync engine, aborts what is in flight,
 * detaches every wake and resets the attendance and sync stores. It never
 * touches IndexedDB data — queued punches stay on the device for the same
 * account to resume.
 */
export function stopOfflineAttendance(): void {
  generation += 1;
  const s = session;
  session = null;
  if (s !== null) {
    s.abort.abort();
    s.provisionAbort?.abort();
    clearProvisionTimer(s);
    for (const detach of s.detach.splice(0)) {
      try {
        detach();
      } catch (err) {
        // A listener that will not detach must not stop the sign-out.
        if (IS_DEV) console.warn('[offline-attendance] could not detach a wake', errorName(err));
      }
    }
    const engine = s.engine;
    s.engine = null;
    // Best effort: stop() never rejects by contract, and a stopped engine changes nothing either way.
    if (engine !== null) void engine.stop().catch(() => undefined);
  }
  endSettling();
  const pinia = getActivePinia();
  if (pinia !== undefined) {
    useAttendanceStore(pinia).reset();
    useSyncStore(pinia).reset();
  }
}

/**
 * Ask the sync engine for a pass; `force` skips the queue head's back-off and
 * a pause ("Sync now"). `_reason` names the caller (`'manual'`, `'capture'`…)
 * for readability at the call site; it changes nothing.
 */
export function kickOfflineSync(_reason: string, force = false): void {
  const s = session;
  if (s === null || !alive(s)) return;
  s.engine?.kick({ force });
  if (force) {
    maybeProvision(s, true);
    void refreshLocal(s);
  }
}

/**
 * Resolves when no capture commit and no settlement is running (an app update
 * waits for it, bounded by the caller — `setUpdateQuiesce()` in `pwa/register.ts`).
 * Never rejects.
 */
export async function offlineQuiesce(): Promise<void> {
  const waits: Array<Promise<unknown>> = [...busyWork];
  if (settling !== null) waits.push(settling.promise);
  await Promise.allSettled(waits);
}

/** Unsettled punches on this DEVICE, every scope (no decryption) — for the sign-in page. Never rejects. */
export async function deviceUnsettledCount(): Promise<number> {
  if (!isIndexedDbAvailable()) return 0;
  try {
    // A device that never used offline attendance has nothing waiting — and
    // counting must not CREATE the database there (Dexie opens on first use).
    if (!(await offlineDbMayExist())) return 0;
    return await countUnsettled();
  } catch {
    // Unreadable storage: nothing to promise on the sign-in page.
    return 0;
  }
}

/**
 * Save one clock action for the signed-in session (the store's `capture()`).
 * Never throws; only `queued` means saved on the device and `online` means
 * ERPat confirmed it. See the module docblock for the mode.
 */
export async function captureAttendance(input: CaptureInput): Promise<CaptureOutcome> {
  const s = session;
  if (s === null || !alive(s)) return refused('signed_out');
  const run = (async (): Promise<CaptureOutcome> => {
    try {
      await s.started;
      // A re-resolution may start at any await — before the lock, or while the
      // chain is re-read inside it. Either way the capture waits for it to
      // finish and decides again on what it loaded; twice in a row ⇒ busy.
      for (let attempt = 0; attempt < CAPTURE_ATTEMPTS; attempt += 1) {
        if (s.resolving !== null) await s.resolving;
        if (!alive(s)) return refused('signed_out');
        const outcome = await withCaptureLock(s, () => captureLocked(s, input));
        if (outcome !== RESOLVING) return outcome;
      }
      return refused('busy');
    } catch (err) {
      // A bug, not an outcome: nothing may read as saved.
      if (IS_DEV) console.warn('[offline-attendance] capture failed unexpectedly', errorName(err));
      return refused('storage');
    }
  })();
  busyWork.add(run);
  void run.then(
    () => busyWork.delete(run),
    () => busyWork.delete(run),
  );
  return run;
}

/** For specs: stop, and forget the clock monitor and everything process-wide. */
export function resetOfflineAttendanceForTests(): void {
  stopOfflineAttendance();
  for (const detach of monitorDetach.splice(0)) detach();
  monitor = null;
  appPaused = false;
  busyWork.clear();
  endSettling();
}

/* ── Session lifecycle ─────────────────────────────────────────────────── */

function newSession(gen: number, epoch: number): OfflineSession {
  const environmentSupported = supportedEnvironment();
  return {
    gen,
    epoch,
    started: Promise.resolve(),
    loaded: false,
    captureAllowed:
      OFFLINE_ATTENDANCE_ENABLED && (!nativePlatform() || OFFLINE_ATTENDANCE_NATIVE_ENABLED) && environmentSupported,
    environmentSupported,
    scope: null,
    scopeFailed: false,
    key: null,
    keyState: 'unknown',
    chainKeyMismatch: false,
    storage: 'unknown',
    storagePersisted: null,
    noPriorData: false,
    dormant: false,
    backend: 'unknown',
    notReadyAnswer: false,
    context: null,
    contextLoaded: false,
    integrity: 'ok',
    pinFailedScopeKey: null,
    pinRecovery: false,
    pinRecoveryAgain: false,
    onlinePosts: 0,
    confirmedAfterPosts: 0,
    snapshot: null,
    chain: null,
    unsettled: Number.NaN,
    oldestPendingAt: null,
    engine: null,
    failingSinceDeviceMs: null,
    provisioning: null,
    provisionRun: 0,
    provisionAbort: null,
    provisionTimer: null,
    provisionAttempt: 0,
    lastProvisionAt: null,
    lastOnlineCaptureAt: null,
    readSeq: 0,
    appliedReadSeq: 0,
    localRefresh: null,
    localRerun: false,
    resolving: null,
    resolveAgain: false,
    suspended: false,
    wakesAttached: false,
    storageCheck: false,
    keyRetry: false,
    abort: new AbortController(),
    detach: [],
  };
}

/** The session is still the current one: same orchestrator generation, same auth epoch. */
function alive(s: OfflineSession): boolean {
  return session === s && s.gen === generation && getSessionEpoch() === s.epoch;
}

async function boot(s: OfflineSession): Promise<void> {
  clockMonitor();
  s.detach.push(
    onOfflineDbWiped(() => {
      if (alive(s)) void reresolve(s);
    }),
  );

  const gate = await loadGate(s);
  if (gate === 'ended') return;
  if (gate === 'nothing') {
    s.dormant = true;
    s.unsettled = 0;
    finishLoad(s);
    return;
  }

  await resolveAndLoad(s);
  if (!alive(s)) return;
  attachWakes(s);
  finishLoad(s);
  void provisionNow(s);
}

function finishLoad(s: OfflineSession): void {
  if (!alive(s)) return;
  s.loaded = true;
  publish(s);
}

/**
 * The start gate, applied at boot AND before every re-resolution (D6):
 * `nothing` when there is no account to scope by or no IndexedDB at all, or
 * when this build or platform does not capture and nothing is queued on the
 * device (punches queued earlier are always uploaded — but an idle device
 * never gets a database or a data key opened, let alone created, for a session
 * that does not capture). `ended` when the session ended while counting.
 */
async function loadGate(s: OfflineSession): Promise<'load' | 'nothing' | 'ended'> {
  if (tokenIdentity() === null || !isIndexedDbAvailable()) return 'nothing';
  if (s.captureAllowed) return 'load';
  const queued = await deviceUnsettledCountOrNaN();
  if (!alive(s)) return 'ended';
  return queued === 0 ? 'nothing' : 'load';
}

/**
 * Resolve the data key, the installation id and the scope TOGETHER, then load
 * what the device holds for the scope, restore the clock monitor and build the
 * sync engine. Used at start and after a wipe. A count of what waits that the
 * failing store could not give is settled last (see {@link countWhatMayWait}).
 */
async function resolveAndLoad(s: OfflineSession): Promise<void> {
  // FIRST, before the probe, the key or the scope opens (and so creates) the
  // database: did this device hold any offline data at all?
  s.noPriorData = !(await offlineDbMayExist());
  if (!alive(s)) return;

  // The probe asks the browser to persist storage: only when this build captures.
  const healthRead: Promise<StorageHealth | null> = s.captureAllowed
    ? bounded(probeStorageHealth(), STORAGE_PROBE_TIMEOUT_MS, PROBE_TIMED_OUT)
    : Promise.resolve(null);
  const [health, keyState] = await Promise.all([
    healthRead,
    bounded(loadDataKey({ create: s.captureAllowed }), KEY_LOAD_TIMEOUT_MS, KEY_TIMED_OUT),
  ]);
  if (!alive(s)) return;
  if (health !== null) {
    s.storage = health.status === 'ready' ? 'ready' : 'failed';
    s.storagePersisted = health.persisted;
  }
  s.keyState = keyState.status;
  s.key = keyState.status === 'ready' ? { key: keyState.key, keyId: keyState.keyId } : null;
  s.chainKeyMismatch = false;
  s.contextLoaded = false;

  let scope: OfflineScope | null = null;
  s.scopeFailed = false;
  try {
    scope = await resolveScope();
  } catch (err) {
    // IndexedDB failed reading (or minting) the installation id.
    if (!alive(s)) return;
    s.scopeFailed = true;
    markStorageFailure(s, err);
  }
  if (!alive(s)) return;
  s.scope = scope;

  // The clock state this device kept for this API origin (C11).
  const persisted = await readClockState();
  if (!alive(s)) return;

  const key = s.key;
  if (scope !== null && key === null) {
    // Without the key nothing sealed opens; still count what waits (no decryption).
    s.unsettled = await scopeUnsettledOrNaN(scope.scopeKey);
    if (!alive(s)) return;
  } else if (scope !== null && key !== null) {
    try {
      const local = await loadLocal(scope, key);
      if (!alive(s)) return;
      s.context = local.context;
      s.integrity = local.integrity;
      s.snapshot = newerSnapshot(s.snapshot, local.snapshot);
      s.contextLoaded = true;
    } catch (err) {
      // IndexedDB failed while loading (never a corrupt record: loadLocal reports
      // those). The context stays unread — and nothing is provisioned — until
      // storage recovers and the session is re-resolved (recheckStorage())…
      if (!alive(s)) return;
      markStorageFailure(s, err);
      if (s.noPriorData) {
        // …unless the database did not exist when this resolution began: then
        // nothing can be stored for the scope, its context is known — absent —
        // without the read, and a provisioning has no pin to keep (D3).
        s.context = null;
        s.integrity = 'ok';
        s.contextLoaded = true;
      }
    }
  }

  // C11 — this session's thresholds FIRST (the stored policy's, else the
  // defaults: never the previous account's), then restore from both the
  // persisted clock state and the stored context's calibration.
  const m = clockMonitor();
  const policy = s.integrity === 'ok' ? (s.context?.policy ?? null) : null;
  m.setThresholds(policy !== null ? thresholdsOf(policy) : DEFAULT_THRESHOLDS);
  m.restore(persisted.lastSeenWallMs, persisted.calibration);
  m.restore(persisted.lastSeenWallMs, s.context?.calibration ?? null);

  if (scope !== null && key !== null) {
    await reloadChain(s);
    if (!alive(s)) return;
    buildEngine(s);
  }
  await countWhatMayWait(s);
}

/**
 * D9 — the queue count a failing store could not give (the scope, the chain or
 * the count failed: `unsettled` is still `NaN`). Readiness takes an unknown
 * count as punches waiting and never opens ERPat's own POST; but where NO punch
 * can be waiting that would refuse every clock action — online ones included —
 * to an employee who clocked online before offline punches existed. So: `0`
 * when the database did not exist before this resolution (nothing was ever
 * queued on this device), else when a device-wide count — every scope, nothing
 * decrypted — succeeds with `0`. Anything else stays unknown: a device-wide
 * count above zero may be another account's punches, but without the scope it
 * cannot tell. The count is only the half that keeps the queue from blocking:
 * ERPat saying capture is off (readiness rule 5), or a device that held nothing
 * (readiness's 7–8 exception), is what opens the POST.
 */
async function countWhatMayWait(s: OfflineSession): Promise<void> {
  if (Number.isFinite(s.unsettled)) return;
  const counted = s.noPriorData ? 0 : await deviceUnsettledCountOrNaN();
  if (!alive(s) || Number.isFinite(s.unsettled)) return;
  if (counted === 0) s.unsettled = 0;
}

/**
 * Stop capture and re-resolve the key, the installation id and the scope
 * together (the database was wiped, re-created or answered for another world),
 * then rebuild the engine and provision again. Coalesced: one re-resolution at
 * a time, re-run once when asked again meanwhile. Each run applies the start
 * gate first, exactly as boot does (D6): a session boot decided must not load
 * — no account, no IndexedDB, or no capture with nothing queued — loads
 * nothing here either (no key, no database, no engine, no provisioning).
 */
function reresolve(s: OfflineSession): Promise<void> {
  if (s.resolving !== null) {
    s.resolveAgain = true;
    return s.resolving;
  }
  const run = (async (): Promise<void> => {
    await s.started;
    do {
      s.resolveAgain = false;
      if (!alive(s)) return;
      s.suspended = true;
      const engine = s.engine;
      s.engine = null;
      // Best effort: stop() never rejects by contract, and the new scope gets its own engine.
      if (engine !== null) void engine.stop().catch(() => undefined);
      orphanProvisioning(s); // its answer is for the old scope
      s.scope = null;
      s.key = null;
      s.keyState = 'unknown';
      s.context = null;
      s.contextLoaded = false;
      s.integrity = 'ok';
      s.snapshot = null;
      s.chain = null;
      s.unsettled = Number.NaN;
      s.oldestPendingAt = null;
      publish(s);
      const gate = await loadGate(s);
      if (gate === 'ended' || !alive(s)) return;
      s.dormant = gate === 'nothing';
      if (s.dormant) {
        s.unsettled = 0;
        continue; // to the loop condition: nothing to resolve, unless asked again meanwhile
      }
      await resolveAndLoad(s);
    } while (s.resolveAgain && alive(s));
    if (!alive(s)) return;
    s.suspended = false;
    s.loaded = true;
    if (s.dormant) {
      publish(s);
      return;
    }
    attachWakes(s);
    publish(s);
    void provisionNow(s);
  })()
    .catch((err: unknown) => {
      // A bug, not an outcome: the next wake tries again.
      if (IS_DEV) console.warn('[offline-attendance] re-resolution failed', errorName(err));
    })
    .finally(() => {
      if (s.resolving === run) s.resolving = null;
      s.suspended = false;
    });
  s.resolving = run;
  return run;
}

/* ── Sync engine ───────────────────────────────────────────────────────── */

function buildEngine(s: OfflineSession): void {
  const { scope, key } = s;
  if (scope === null || key === null || s.engine !== null) return;
  // `postSync`, opening the settlement window (quiesce) from an answer to the
  // engine's next phase report — for THIS engine only: a late answer to an
  // engine that was stopped or replaced settles nothing, and no report of its
  // would ever close the window.
  const upload = async (body: SyncRequestV1, signal?: AbortSignal): Promise<unknown> => {
    const response = await postSync(body, signal);
    if (alive(s) && s.engine === engine) beginSettling();
    return response;
  };
  const engine: SyncEngine = new SyncEngine({
    scope,
    key,
    ownerId: newUuidV4(),
    isCurrent: () => alive(s) && s.engine === engine,
    api: { postSync: upload },
    now: () => Date.now(),
    random: Math.random,
    onSettled: () => {
      if (alive(s) && s.engine === engine) void afterSettlement(s);
    },
    onPhase: (report) => {
      endSettling();
      if (alive(s) && s.engine === engine) onEnginePhase(s, report);
    },
  });
  s.engine = engine;
  engine.kick();
}

function onEnginePhase(s: OfflineSession, report: SyncPhaseReport): void {
  const pinia = getActivePinia();
  if (pinia !== undefined) useSyncStore(pinia).setPhase(report);
  // Readiness's sync_failing: since when uploads keep failing (a success or an empty queue ends it).
  if (report.phase === 'idle') s.failingSinceDeviceMs = null;
  else if (
    s.failingSinceDeviceMs !== null &&
    report.lastSuccessAt !== null &&
    report.lastSuccessAt >= s.failingSinceDeviceMs
  ) {
    s.failingSinceDeviceMs = null;
  }
  const failing =
    (report.phase === 'offline' || report.phase === 'waiting' || report.phase === 'paused') &&
    report.lastErrorCode !== null;
  if (failing && s.failingSinceDeviceMs === null) s.failingSinceDeviceMs = Date.now();
  // Rows moved in or out of flight: the chain the store shows follows.
  void refreshLocal(s);
}

/** A settlement: refresh the confirmed state first, then prune with its `as_of` (D13, D14), then re-read. */
async function afterSettlement(s: OfflineSession): Promise<void> {
  const { scope, key } = s;
  if (scope === null || key === null) return;
  await refreshConfirmed(s, scope, key);
  if (!alive(s) || !sameScope(s, scope)) return;
  const snapshot = s.snapshot;
  if (snapshot !== null) {
    try {
      await pruneSettled(scope.scopeKey, Date.now(), snapshot.asOfMs);
    } catch (err) {
      // Best effort: pruning only reclaims space; the rows stay correct.
      if (IS_DEV) console.warn('[offline-attendance] pruning failed', errorName(err));
    }
  }
  if (!alive(s)) return;
  await refreshLocal(s);
}

/**
 * `GET sync/state` into the session (the newest snapshot wins). An `ok` answer
 * to a request sent after an online punch confirms the base that punch moved
 * (C20). Never rejects.
 */
async function refreshConfirmed(s: OfflineSession, scope: OfflineScope, key: DataKeyReady): Promise<void> {
  const postsAtStart = s.onlinePosts;
  let result: StateResult;
  try {
    result = await refreshState(scope, key, s.abort.signal);
  } catch (err) {
    // The repository failed saving or loading.
    if (alive(s)) markStorageFailure(s, err);
    return;
  }
  if (!alive(s) || !sameScope(s, scope)) return;
  switch (result.kind) {
    case 'ok':
      s.snapshot = newerSnapshot(s.snapshot, result.snapshot);
      s.confirmedAfterPosts = Math.max(s.confirmedAfterPosts, postsAtStart);
      // Capture was switched off — or on — since the stored context: recording
      // that (and, when on, getting a grant) is the provisioning's job. Not when
      // ERPat last said it is NOT READY: sync/state checks fewer things than
      // offline-context, and that answer is asked again on its own timer (C21).
      if (result.capability !== (s.context?.capability.offlineAttendance === true) && !reaskedSlowly(s)) {
        void provisionNow(s);
      }
      break;
    case 'stale':
      if (result.reason === 'database') void reresolve(s);
      break;
    case 'forbidden':
      s.backend = 'forbidden';
      break;
    case 'unsupported':
    case 'module_unavailable':
      s.backend = 'unsupported';
      break;
    case 'transport':
    case 'invalid':
      // Keep the last snapshot; a later wake or settlement asks again.
      break;
    default: {
      const exhaustive: never = result;
      return exhaustive;
    }
  }
  publish(s);
}

/**
 * C21 — ERPat's last word on the capability is "not ready": the stored context
 * says `{false, 'not_ready'}`, or this session's latest provisioning answer
 * said so (saved or not — `notReadyAnswer`). That is asked again every
 * `OFFLINE_CONTEXT_REFRESH_MS`, and a sync/state answer that differs — after
 * every online punch and every settlement — never hurries it. A missing route
 * or module is not "not ready": a sync/state that now answers is provisioned
 * at once.
 */
function reaskedSlowly(s: OfflineSession): boolean {
  if (s.notReadyAnswer) return true;
  const capability = s.integrity === 'ok' ? s.context?.capability : undefined;
  return capability !== undefined && !capability.offlineAttendance && capability.reason === 'not_ready';
}

/* ── Provisioning ──────────────────────────────────────────────────────── */

/**
 * `GET offline-context` for the session's scope (single flight). Only when
 * this build captures, and only once the stored context was read (or is known
 * to be absent — see `contextLoaded`): it is the `prior` that carries the
 * pinned issuer and employee (D3). On success, with NO unrelated await in
 * between (C11): the new policy's thresholds, then the new calibration. A save
 * that fails still says what ERPat said about the capability
 * (`unsavedCapability()`), and readiness follows it.
 */
function provisionNow(s: OfflineSession): Promise<void> {
  if (!alive(s) || !s.captureAllowed || s.suspended || !s.contextLoaded) return Promise.resolve();
  const { scope, key } = s;
  if (scope === null || key === null) return Promise.resolve();
  if (s.provisioning !== null) return s.provisioning;
  clearProvisionTimer(s);
  s.lastProvisionAt = Date.now();
  const controller = new AbortController();
  s.provisionAbort = controller;
  const onSessionAbort = (): void => controller.abort();
  s.abort.signal.addEventListener('abort', onSessionAbort, { once: true });
  // The stored context as loaded — also after a pin failure (C19): re-asking
  // with the same pin never re-pins while punches wait; only discarding the
  // record (an empty queue) lets a provisioning start fresh.
  const prior = s.integrity === 'ok' ? s.context : null;
  const postsAtStart = s.onlinePosts;
  const runId = ++s.provisionRun;

  const run: Promise<void> = (async () => {
    let result: ProvisionResult;
    try {
      result = await provision(scope, key, prior, controller.signal);
    } catch (err) {
      // Only the repository rejects: IndexedDB failed while saving.
      if (!alive(s) || s.provisionRun !== runId) return;
      // ERPat's answer was verified before the save failed: what it said about
      // the capability holds although this device could not keep it (C21's
      // mapping) — a company that turned capture off keeps its online POST
      // (readiness rule 5), one that turned it on stays blocked by the storage.
      const capability = sameScope(s, scope) ? unsavedCapability(err) : null;
      if (capability !== null) {
        s.backend = backendOfCapability(capability);
        s.notReadyAnswer = !capability.offlineAttendance && capability.reason === 'not_ready';
      }
      markStorageFailure(s, err);
      if (capability !== null && !capability.offlineAttendance) {
        // An answer that capture is off is asked again only after the refresh interval (C21).
        s.provisionAttempt = 0;
        scheduleProvision(s, OFFLINE_CONTEXT_REFRESH_MS);
      } else {
        scheduleProvision(s, syncBackoffMs(++s.provisionAttempt));
      }
      return;
    }
    if (!alive(s) || s.provisionRun !== runId || !sameScope(s, scope)) return;
    applyProvision(s, scope, result, postsAtStart);
  })()
    .catch((err: unknown) => {
      // A bug, not an outcome: try again later.
      if (IS_DEV) console.warn('[offline-attendance] provisioning failed unexpectedly', errorName(err));
    })
    .finally(() => {
      s.abort.signal.removeEventListener('abort', onSessionAbort);
      if (s.provisionAbort === controller) s.provisionAbort = null;
      if (s.provisionRun === runId) s.provisioning = null;
      publish(s);
    });
  s.provisioning = run;
  publish(s);
  return run;
}

function applyProvision(s: OfflineSession, scope: OfflineScope, result: ProvisionResult, postsAtStart: number): void {
  switch (result.kind) {
    case 'enabled':
    case 'disabled': {
      // C11 — nothing awaited between the answer and these two calls.
      const m = clockMonitor();
      const policy = result.record.policy;
      if (policy !== null) m.setThresholds(thresholdsOf(policy));
      m.calibrated(result.record.calibration);
      s.context = result.record;
      s.integrity = 'ok';
      s.pinFailedScopeKey = null;
      s.snapshot = newerSnapshot(s.snapshot, result.snapshot);
      // C20: the answer to a request sent after an online punch carries the base it moved.
      s.confirmedAfterPosts = Math.max(s.confirmedAfterPosts, postsAtStart);
      // C21: capture switched on ⇒ enabled; off and NOT READY (the tenant cannot
      // issue grants yet) ⇒ unsupported — readiness online_only/unsupported_backend,
      // re-asked only every OFFLINE_CONTEXT_REFRESH_MS, never on every tick;
      // off by the company ⇒ disabled.
      const notReady = result.kind === 'disabled' && result.record.capability.reason === 'not_ready';
      s.backend = result.kind === 'enabled' ? 'enabled' : notReady ? 'unsupported' : 'disabled';
      s.notReadyAnswer = notReady;
      s.provisionAttempt = 0;
      if (notReady) scheduleProvision(s, OFFLINE_CONTEXT_REFRESH_MS);
      void persistClockState();
      // Clears a paused engine and kicks it: punches the snapshot already holds settle as duplicates.
      s.engine?.resume();
      void refreshLocal(s);
      return;
    }
    case 'unsupported':
    case 'module_unavailable':
      s.backend = 'unsupported';
      s.notReadyAnswer = false;
      scheduleProvision(s, OFFLINE_CONTEXT_REFRESH_MS);
      return;
    case 'forbidden':
      s.backend = 'forbidden';
      scheduleProvision(s, OFFLINE_CONTEXT_REFRESH_MS);
      return;
    case 'transport': {
      const { error } = result;
      // The session is ending (or this request was stopped): its successor asks for itself.
      if (error.code === 'session_expired' || error.code === 'no_session' || error.cancelled) return;
      const backoff = syncBackoffMs(++s.provisionAttempt);
      scheduleProvision(s, error.retryAfterMs !== null ? Math.max(error.retryAfterMs, backoff) : backoff);
      return;
    }
    case 'invalid':
      if (IS_DEV) console.warn('[offline-attendance] provisioning answer refused', result.reason);
      // A timing problem passes; an answer that cannot be trusted is asked for again much later.
      scheduleProvision(
        s,
        result.reason === 'calibration' ? syncBackoffMs(++s.provisionAttempt) : OFFLINE_SYNC_PAUSE_RECHECK_MS,
      );
      if (result.reason === 'issuer_mismatch' || result.reason === 'employee_mismatch') {
        // C19: the stored pin no longer matches what ERPat issues for this account.
        s.pinFailedScopeKey = scope.scopeKey;
        publish(s);
        void recoverPinFailure(s, scope);
      }
      return;
    case 'stale':
      if (result.reason === 'database') void reresolve(s);
      return;
    default: {
      const exhaustive: never = result;
      return exhaustive;
    }
  }
}

function scheduleProvision(s: OfflineSession, delayMs: number): void {
  if (!alive(s)) return;
  clearProvisionTimer(s);
  s.provisionTimer = setTimeout(() => {
    s.provisionTimer = null;
    void provisionNow(s);
  }, delayMs);
}

function clearProvisionTimer(s: OfflineSession): void {
  if (s.provisionTimer !== null) clearTimeout(s.provisionTimer);
  s.provisionTimer = null;
}

/** Abandon the provisioning in flight and the scheduled one: an answer to it changes nothing. */
function orphanProvisioning(s: OfflineSession): void {
  s.provisionAbort?.abort();
  s.provisionRun += 1;
  s.provisioning = null;
  clearProvisionTimer(s);
}

/** ERPat refused the CURRENT scope's stored pin (C19). */
function pinFailed(s: OfflineSession): boolean {
  return s.pinFailedScopeKey !== null && s.scope !== null && s.scope.scopeKey === s.pinFailedScopeKey;
}

/**
 * C19 — pin-failure recovery. Once no capture of this tab is running, the
 * stored context record — ONLY that record, never outbox rows, receipts or
 * the snapshot — is discarded IF the scope has no unsettled punch (checked in
 * the same IndexedDB transaction as the delete), and a fresh provisioning
 * pins the issuer and the employee again. With punches waiting nothing is
 * discarded or re-pinned: readiness stays blocked/scope_mismatch while they
 * upload, and the chain re-read that sees the queue empty runs this again.
 * Single flight; asked again while it runs, it looks once more when done (the
 * queue may have emptied after its check). Never rejects.
 */
async function recoverPinFailure(s: OfflineSession, scope: OfflineScope): Promise<void> {
  if (s.pinRecovery) {
    s.pinRecoveryAgain = true;
    return;
  }
  s.pinRecovery = true;
  try {
    do {
      s.pinRecoveryAgain = false;
      // A capture of this tab still running decided on the old context: let it commit (or not) first.
      await Promise.allSettled([...busyWork]);
      if (!alive(s) || !sameScope(s, scope) || !pinFailed(s)) return;
      let discarded: boolean;
      try {
        discarded = await discardContextIfQueueEmpty(scope.scopeKey);
      } catch (err) {
        // IndexedDB failed: the pin stays refused; the next refusal or chain re-read tries again.
        if (alive(s)) markStorageFailure(s, err);
        return;
      }
      if (!alive(s) || !sameScope(s, scope) || !pinFailed(s)) return;
      if (discarded) {
        s.context = null;
        s.integrity = 'ok';
        s.pinFailedScopeKey = null;
        // A provisioning still out asked with the old pin: its answer must not count.
        orphanProvisioning(s);
        publish(s);
        void provisionNow(s);
        return;
      }
      publish(s);
    } while (s.pinRecoveryAgain);
  } finally {
    s.pinRecovery = false;
    s.pinRecoveryAgain = false;
  }
}

/**
 * Provision when the session needs it: nothing usable stored, ERPat not heard
 * from this session, a context older than `OFFLINE_CONTEXT_REFRESH_MS`, or an
 * UNSAFE clock (C11: only an online calibration cures it — readiness blocks
 * capture, online too, until then). A refusal by ERPat — and a tenant that is
 * not ready (C21) — is re-asked only after the refresh interval. `urgent`
 * (connectivity came back, the app resumed, the user asked) skips a scheduled
 * back-off. Never before the stored context was read (see provisionNow()).
 */
function maybeProvision(s: OfflineSession, urgent: boolean): void {
  if (!alive(s) || !s.captureAllowed || s.dormant || !s.contextLoaded) return;
  if (s.scope === null || s.key === null || s.provisioning !== null) return;
  if (s.provisionTimer !== null && !urgent) return;
  if (!urgent && isOffline.value) return;
  const now = Date.now();
  const since = s.lastProvisionAt === null ? Number.POSITIVE_INFINITY : now - s.lastProvisionAt;
  if (!urgent && since < PROVISION_MIN_SPACING_MS) return;
  if (s.backend === 'unsupported' || s.backend === 'forbidden') {
    if (since > OFFLINE_CONTEXT_REFRESH_MS) void provisionNow(s);
    return;
  }
  const context = s.integrity === 'ok' ? s.context : null;
  const due =
    context === null ||
    s.backend === 'unknown' ||
    now - context.provisionedAtMs > OFFLINE_CONTEXT_REFRESH_MS ||
    clockMonitor().verdict().status === 'unsafe';
  if (due) void provisionNow(s);
}

/* ── Wakes ─────────────────────────────────────────────────────────────── */

function attachWakes(s: OfflineSession): void {
  if (s.wakesAttached || typeof window === 'undefined') return;
  s.wakesAttached = true;
  const listen = (target: EventTarget, type: string, handler: (event: Event) => void): void => {
    target.addEventListener(type, handler);
    s.detach.push(() => target.removeEventListener(type, handler));
  };
  listen(window, 'online', () => wake(s, true));
  listen(window, 'focus', () => wake(s, false));
  listen(window, 'pageshow', (event) => {
    if (Reflect.get(event, 'persisted') === true) wake(s, true);
  });
  if (typeof document !== 'undefined') {
    listen(document, 'visibilitychange', () => {
      if (!documentHidden()) wake(s, false);
    });
  }
  s.detach.push(
    watch(isOffline, (offline, was) => {
      if (was === true && !offline) wake(s, true);
    }),
  );
  // The native resume. Race-safe: detached before the plugin answered ⇒ detach then.
  let attached = true;
  let off: (() => void) | null = null;
  void appState
    .onAppStateChange((active) => {
      if (active) wake(s, true);
    })
    .then((detach) => {
      if (attached) off = detach;
      else detach();
    })
    .catch(() => undefined); // best effort: no native plugin ⇒ the web wakes above suffice
  s.detach.push(() => {
    attached = false;
    off?.();
    off = null;
  });
  const interval = setInterval(() => tick(s), OFFLINE_SYNC_PERIODIC_MS);
  s.detach.push(() => clearInterval(interval));
}

function wake(s: OfflineSession, urgent: boolean): void {
  if (!alive(s) || s.dormant) return;
  // A key that could not be read (a locked iOS keychain reads as absent) is read again on resume.
  if (s.keyState !== 'ready' && s.resolving === null) {
    void retryKey(s);
    return;
  }
  s.engine?.kick();
  maybeProvision(s, urgent);
  void refreshLocal(s);
}

/** The 60 s visible tick: observe the clock, persist it, re-check storage, kick, provision when due, recompute. */
function tick(s: OfflineSession): void {
  if (!alive(s) || s.dormant || documentHidden()) return;
  publish(s); // stamps: observes the clock and recomputes readiness
  void persistClockState();
  // A key that timed out (or was locked) is read again even on a page that gets no focus or visibility events.
  if (s.keyState !== 'ready' && s.resolving === null) void retryKey(s);
  // Storage that failed — or a stored context that could not be read — is checked again.
  const contextUnread = !s.contextLoaded && s.scope !== null && s.key !== null && s.resolving === null;
  if (s.storage === 'failed' || contextUnread) void recheckStorage(s);
  s.engine?.kick();
  maybeProvision(s, false);
}

/**
 * Read the data key again (never recreating one the marker vouches for —
 * `loadDataKey` refuses that by itself). Once it reads, the whole session is
 * re-resolved with it; until then the readiness it already shows stays.
 */
async function retryKey(s: OfflineSession): Promise<void> {
  if (s.keyRetry) return;
  s.keyRetry = true;
  try {
    const state = await bounded(loadDataKey({ create: s.captureAllowed }), KEY_LOAD_TIMEOUT_MS, KEY_TIMED_OUT);
    if (!alive(s)) return;
    if (state.status === 'ready') {
      void reresolve(s);
    } else if (state.status !== s.keyState) {
      s.keyState = state.status;
      publish(s);
    }
  } finally {
    s.keyRetry = false;
  }
}

/**
 * Probe storage again; once it works, load what the failure kept unread. With
 * no scope or key — or a stored context never read (its load failed) — the
 * whole session is re-resolved: that reads the context with its pinned issuer
 * and employee BEFORE anything is provisioned (D3). Otherwise the chain and
 * the snapshot are re-read.
 */
async function recheckStorage(s: OfflineSession): Promise<void> {
  if (s.storageCheck || !s.captureAllowed) return;
  s.storageCheck = true;
  try {
    const health = await bounded(probeStorageHealth(), STORAGE_PROBE_TIMEOUT_MS, PROBE_TIMED_OUT);
    if (!alive(s) || health.status !== 'ready') return;
    s.storage = 'ready';
    s.storagePersisted = health.persisted;
    if (s.scope === null || s.key === null || !s.contextLoaded) void reresolve(s);
    else void refreshLocal(s);
  } finally {
    s.storageCheck = false;
  }
}

/* ── Local reads ───────────────────────────────────────────────────────── */

/** Re-read the chain and the snapshot and publish (single flight; re-run once when asked again). Never rejects. */
function refreshLocal(s: OfflineSession): Promise<void> {
  if (s.localRefresh !== null) {
    s.localRerun = true;
    return s.localRefresh;
  }
  const run = (async (): Promise<void> => {
    do {
      s.localRerun = false;
      if (!alive(s)) return;
      await reloadChain(s);
    } while (s.localRerun && alive(s));
  })()
    .catch((err: unknown) => {
      // reloadChain never rejects; a bug must not leave an unhandled rejection.
      if (IS_DEV) console.warn('[offline-attendance] local refresh failed', errorName(err));
    })
    .finally(() => {
      if (s.localRefresh === run) s.localRefresh = null;
    });
  s.localRefresh = run;
  return run;
}

/**
 * Read the scope's chain, snapshot and oldest unsettled punch NOW and apply
 * them (unless a newer read already landed), then publish. Never rejects:
 * a failure marks storage (or the key) as failed.
 */
async function reloadChain(s: OfflineSession): Promise<void> {
  const { scope, key } = s;
  if (scope === null || key === null) {
    publish(s);
    return;
  }
  const seq = ++s.readSeq;
  try {
    const chain = await listChain(key, scope.scopeKey);
    let snapshot: ConfirmedSnapshotRecord | null = null;
    try {
      snapshot = await loadSnapshot(key, scope.scopeKey);
    } catch (err) {
      // An unreadable snapshot is none (the next provisioning stores a fresh one); anything else is storage.
      if (!isStorageError(err, 'corrupt')) throw err;
    }
    const oldest = await oldestUnsettledCreatedAt(scope.scopeKey);
    if (!alive(s) || !sameScope(s, scope) || seq < s.appliedReadSeq) return;
    s.appliedReadSeq = seq;
    s.chain = chain;
    s.chainKeyMismatch = false;
    s.snapshot = newerSnapshot(s.snapshot, snapshot);
    s.unsettled = chain.filter((event) => event.status !== 'final').length;
    s.oldestPendingAt = oldest;
  } catch (err) {
    // The chain does not open (another key) or IndexedDB failed.
    if (!alive(s) || !sameScope(s, scope)) return;
    markStorageFailure(s, err);
    return;
  }
  publish(s);
  // C19: the last waiting punch of a scope whose pin ERPat refused has settled.
  if (pinFailed(s) && s.unsettled === 0) void recoverPinFailure(s, scope);
}

/* ── Capture ───────────────────────────────────────────────────────────── */

/**
 * What a locked capture answers while the database is being re-resolved: the
 * caller waits for the re-resolution and tries again, under the new scope's lock.
 */
const RESOLVING = 'resolving';
type LockedOutcome = CaptureOutcome | typeof RESOLVING;

/** How often a capture tries before a re-resolution that keeps restarting reads as busy. */
const CAPTURE_ATTEMPTS = 2;

/** The session's key, installation id and scope are being re-resolved (or about to be). */
function reresolving(s: OfflineSession): boolean {
  return s.suspended || s.resolving !== null;
}

/** D9: captures go to the device queue first. */
function queueFirstState(readiness: OfflineReadiness): boolean {
  return readiness.state === 'ready' || readiness.state === 'degraded';
}

/** C20: an online punch ERPat confirmed is not yet reflected by a confirmed snapshot read after it. */
function awaitingOnlineConfirmation(s: OfflineSession): boolean {
  return s.confirmedAfterPosts < s.onlinePosts;
}

/** Run `work` under the cross-tab capture lock when the browser has Web Locks; `busy` when another tab holds it. */
async function withCaptureLock(s: OfflineSession, work: () => Promise<LockedOutcome>): Promise<LockedOutcome> {
  const locks = webLocks();
  const scope = s.scope;
  if (locks === null || scope === null) return work();
  // A holder, not two locals: the callback assigns them, which control-flow narrowing cannot see.
  const held: { ran: boolean; outcome: LockedOutcome | null } = { ran: false, outcome: null };
  try {
    await locks.request(`syntry-attendance-capture:${scope.scopeKey}`, { ifAvailable: true }, async (lock) => {
      if (lock === null) return;
      held.ran = true;
      held.outcome = await work();
    });
  } catch (err) {
    // The Web Locks API failed before the capture ran: capture without it (the
    // commit's head/tip check still catches a concurrent tab). After it ran,
    // `work` never throws, so this is the API failing on release: keep the outcome.
    if (!held.ran) return work();
    if (IS_DEV) console.warn('[offline-attendance] capture lock failed', errorName(err));
  }
  if (!held.ran) return refused('busy');
  return held.outcome ?? refused('storage');
}

/**
 * C13: re-read inside the lock, re-project from ONE stamp, then decide the mode
 * — on the COMPUTED readiness (D9). A re-resolution in progress, or one that
 * began while the chain was re-read, answers {@link RESOLVING}: what this
 * capture read may belong to a database that is gone, and the display override
 * ("being set up again") must never be taken for the online path while punches
 * are queued.
 */
async function captureLocked(s: OfflineSession, input: CaptureInput): Promise<LockedOutcome> {
  if (reresolving(s)) return RESOLVING;
  if (s.scope !== null && s.key !== null) {
    await reloadChain(s);
    if (!alive(s)) return refused('signed_out');
    if (reresolving(s)) return RESOLVING;
  }
  let stamp = clockMonitor().stamp();
  let view = evaluate(s, stamp);
  publishView(s, view);
  if (queueFirstState(view.computed) && awaitingOnlineConfirmation(s)) {
    // C20: an online punch moved ERPat's base after the confirmed snapshot this
    // capture would build on was read. Ask ERPat (inside the lock, bounded);
    // until a snapshot read AFTER that punch is stored, nothing is queued.
    const { scope, key } = s;
    if (scope !== null && key !== null) {
      await bounded(refreshConfirmed(s, scope, key), ONLINE_CONFIRM_WAIT_MS, undefined);
    }
    if (!alive(s)) return refused('signed_out');
    if (reresolving(s)) return RESOLVING;
    if (awaitingOnlineConfirmation(s)) return refused('busy');
    stamp = clockMonitor().stamp();
    view = evaluate(s, stamp);
    publishView(s, view);
  }
  // From here to the decision, nothing is awaited.
  const { computed } = view;
  if (queueFirstState(computed)) return queueCapture(s, input, view, stamp);
  // Readiness's queue override guarantees these two imply an EMPTY queue (D9).
  if (computed.state === 'online_only' || computed.state === 'preparing') return onlineCapture(s, input);
  return refused(computed.reason ?? 'storage_unavailable');
}

async function queueCapture(
  s: OfflineSession,
  input: CaptureInput,
  view: View,
  stamp: ClockStamp | null,
): Promise<CaptureOutcome> {
  const { scope, key, context } = s;
  const { projection } = view;
  if (stamp === null) return refused('clock_unsafe');
  if (scope === null || key === null || context === null || projection === null) return refused('not_provisioned');
  // A double tap (two surfaces, a bouncing finger): both times are on ERPat's time base.
  if (projection.lastEventAtMs !== null && stamp.correctedNowMs - projection.lastEventAtMs < OFFLINE_CAPTURE_MIN_GAP_MS) {
    return refused('busy');
  }
  const result = await captureEvent(
    {
      scope,
      key,
      context,
      projection,
      stamp,
      connectivity: connectivityNow(),
      appVersion: APP_VERSION,
      timezone: deviceTimezone(),
    },
    input,
  );
  if (!result.ok) return outcomeOfRefusal(s, result, stamp);
  // Saved. Show it at once (re-read and re-project), then send it.
  await reloadChain(s);
  s.engine?.kick();
  return { kind: 'queued', clientEventId: result.row.clientEventId, occurredAtMs: result.occurredAtMs, action: input.action };
}

/** FE-8's refusal as a capture outcome (each reason's copy). */
function outcomeOfRefusal(
  s: OfflineSession,
  result: Extract<OutboxCaptureResult, { ok: false }>,
  stamp: ClockStamp,
): CaptureOutcome {
  switch (result.reason) {
    case 'not_allowed':
    case 'note_required':
      return refused(result.reason);
    case 'note_too_long':
      return refused('note_too_long', s.context?.policy?.note_max_length);
    case 'state_changed':
      // The chain moved under the capture: show what is there now; never retry the stale capture.
      void refreshLocal(s);
      return refused('state_changed');
    case 'clock_unsafe':
      maybeProvision(s, true);
      return refused('clock_unsafe');
    case 'no_grant': {
      const grant = s.context?.grant ?? null;
      return refused(grant !== null && stamp.correctedNowMs > grant.validUntilMs ? 'grant_expired' : 'not_provisioned');
    }
    case 'storage':
      if (result.wiped) {
        void reresolve(s);
      } else {
        // Review Focus 5: nothing saved, and readiness says storage is unavailable.
        s.storage = 'failed';
        publish(s);
      }
      return refused('storage');
    default: {
      const exhaustive: never = result;
      return exhaustive;
    }
  }
}

/** D9's online path (an empty queue): ERPat's own clock POST. */
async function onlineCapture(s: OfflineSession, input: CaptureInput): Promise<CaptureOutcome> {
  const note = typeof input.note === 'string' ? input.note.trim() : '';
  if (input.action === 'time_out' && note === '') return refused('note_required');
  const pinia = getActivePinia();
  if (pinia !== undefined && useAttendanceStore(pinia).canClockInOutOnline === false) return refused('permission_missing');
  if (s.lastOnlineCaptureAt !== null && Date.now() - s.lastOnlineCaptureAt < OFFLINE_CAPTURE_MIN_GAP_MS) {
    return refused('busy');
  }
  try {
    await onlinePost(input.action, note);
  } catch (err) {
    return { kind: 'refused', reason: 'online_failed', message: onlineFailureMessage(err) };
  }
  s.lastOnlineCaptureAt = Date.now();
  // ERPat's confirmed base moved: no queue-first capture builds on a snapshot
  // read before this punch (C20) — refresh it so a later one can.
  s.onlinePosts += 1;
  const { scope, key } = s;
  if (alive(s) && scope !== null && key !== null) {
    void refreshConfirmed(s, scope, key).then(() => refreshLocal(s));
  }
  return { kind: 'online', action: input.action };
}

async function onlinePost(action: AttendanceEventType, note: string): Promise<void> {
  switch (action) {
    case 'time_in':
      await attendanceService.clockIn();
      return;
    case 'time_out':
      await attendanceService.clockOut({ note });
      return;
    case 'break_out':
      await attendanceService.break('start');
      return;
    case 'break_in':
      await attendanceService.break('end');
      return;
    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}

/** ERPat's own refusal (a 4xx it answered) is worth showing; anything else leaves the outcome unknown (C7). */
function onlineFailureMessage(err: unknown): string {
  if (
    err instanceof ErpatApiError &&
    err.status >= 400 &&
    err.status < 500 &&
    !isForeignResponse(err) &&
    !isSessionCancelled(err)
  ) {
    return errorMessage(err);
  }
  return captureRefusalCopy('online_failed');
}

function refused(reason: RefusalReason, noteMaxLength?: number): CaptureOutcome {
  return { kind: 'refused', reason, message: captureRefusalCopy(reason, noteMaxLength !== undefined ? { noteMaxLength } : {}) };
}

/* ── Readiness, projection, publication ────────────────────────────────── */

interface View {
  /** What the stores show: `computed`, except while the database is re-resolved (see below). */
  readiness: OfflineReadiness;
  /**
   * Readiness as the rules compute it — the ONLY readiness a capture may decide
   * on (D9). Never the display override: "being set up again" is not the
   * online path.
   */
  computed: OfflineReadiness;
  projection: AttendanceProjection | null;
  /** "Now" for the review badge's 7-day window (C18): the stamp's corrected time, else the device's. */
  reviewNowMs: number;
}

/** Readiness and the projection from the session's state and ONE stamp (C12). */
function evaluate(s: OfflineSession, stamp: ClockStamp | null): View {
  const nowMs = stamp?.correctedNowMs ?? Number.NaN;
  // C19: a stored context whose pin ERPat refused is not trusted — exactly
  // like one that failed its binding (readiness rule 9, scope_mismatch).
  const pinRefused = pinFailed(s);
  const integrity: ContextIntegrity = pinRefused ? 'scope_mismatch' : s.integrity;
  const context = integrity === 'ok' ? s.context : null;
  const projection =
    s.chain === null
      ? null
      : project({
          confirmed: s.snapshot,
          chain: s.chain,
          maxBreakEntries: context?.policy?.max_break_entries ?? MAX_BREAK_ENTRIES_DEFAULT,
          nowMs,
          localDayKey,
        });
  const rules = computeReadiness({
    buildEnabled: OFFLINE_ATTENDANCE_ENABLED,
    nativePlatform: nativePlatform(),
    nativeEnabled: OFFLINE_ATTENDANCE_NATIVE_ENABLED,
    environmentSupported: s.environmentSupported,
    storage: s.storage,
    // A device that held no offline data: its failing store keeps ERPat's own POST (rules 7–8 exception).
    noPriorData: s.noPriorData,
    storagePersisted: s.storagePersisted,
    key: s.chainKeyMismatch ? 'lost' : s.keyState,
    authenticated: tokenIdentity() !== null,
    // A scope that failed only because IndexedDB did is a storage problem
    // (rule 8), not a sign-out (rule 4): the identity is there.
    scopeResolved: s.scope !== null || (s.scopeFailed && s.storage === 'failed'),
    backend: s.backend,
    provisioning: s.provisioning !== null,
    context: s.context,
    contextIntegrity: integrity,
    confirmed: s.snapshot,
    clock: stamp?.verdict ?? clockMonitor().verdict(),
    unsettled: s.unsettled,
    // Same time base as nowMs: the device moment shifted by the stamp's offset.
    syncFailingSinceMs:
      s.failingSinceDeviceMs === null || stamp === null
        ? null
        : s.failingSinceDeviceMs + (stamp.correctedNowMs - stamp.deviceNowMs),
    nowMs,
  });
  // C19: with punches waiting, the refused pin itself is the reason (C7 copy) —
  // not "punches are waiting": they keep uploading, and nothing is captured or
  // re-pinned until they have. A more specific blocked reason (key, storage) stays.
  const computed: OfflineReadiness =
    pinRefused && rules.state === 'blocked' && rules.reason === 'queue_waiting'
      ? { state: 'blocked', reason: 'scope_mismatch', warnings: [] }
      : rules;
  // While the database is re-resolved the scope is momentarily unknown: SHOW
  // that as being set up again, not a sign-out. Display only — a capture waits
  // for the re-resolution and decides on `computed` (see captureLocked()).
  const readiness: OfflineReadiness = s.suspended ? { state: 'preparing', reason: 'provisioning', warnings: [] } : computed;
  // C18: the review badge measures a settlement's age on ERPat's time base.
  const reviewNowMs = Number.isFinite(nowMs) ? nowMs : Date.now();
  return { readiness, computed, projection, reviewNowMs };
}

/** Recompute from a fresh stamp and push it to the stores. */
function publish(s: OfflineSession): void {
  if (!alive(s) || !s.loaded) return;
  publishView(s, evaluate(s, clockMonitor().stamp()));
}

function publishView(s: OfflineSession, view: View): void {
  if (!alive(s) || !s.loaded) return;
  const pinia = getActivePinia();
  if (pinia === undefined) return;
  useAttendanceStore(pinia).applyOrchestratorState({
    readiness: view.readiness,
    projection: view.projection,
    // A context that failed its binding, or whose pin ERPat refused (C19), is not handed out.
    context: s.integrity === 'ok' && !pinFailed(s) ? s.context : null,
  });
  const { projection } = view;
  const held = projection?.reviewBlocked.length ?? 0;
  // C18: a refused punch raises the badge for 7 days after ERPat settled it;
  // older ones stay listed on the Sync page (rejected rows are never pruned, D14).
  const rejected =
    projection?.chain.filter((event) => recentlyRejected(event, view.reviewNowMs)).length ?? 0;
  useSyncStore(pinia).setCounts({
    pendingCount: Number.isFinite(s.unsettled) ? Math.max(0, s.unsettled - held) : 0,
    reviewCount: held + rejected,
    oldestPendingAt: s.oldestPendingAt,
  });
}

/**
 * A punch ERPat refused within the last `OFFLINE_ACCEPTED_PRUNE_MS` (7 days),
 * measured from its receipt's `received_at` (ERPat's time, when it settled the
 * punch). A receipt whose time cannot be read counts: never hide a refusal
 * because its date is unclear.
 */
function recentlyRejected(event: ChainEvent, nowMs: number): boolean {
  if (event.status !== 'final' || event.outcome !== 'rejected') return false;
  const settledAtMs = event.receipt === null ? Number.NaN : Date.parse(event.receipt.received_at);
  if (!Number.isFinite(settledAtMs) || !Number.isFinite(nowMs)) return true;
  return nowMs - settledAtMs <= OFFLINE_ACCEPTED_PRUNE_MS;
}

/* ── Clock state persistence (C11) ─────────────────────────────────────── */

interface PersistedClockState {
  lastSeenWallMs: number | null;
  calibration: ClockCalibration | null;
}

const NO_CLOCK_STATE: PersistedClockState = { lastSeenWallMs: null, calibration: null };

/** The API origin the clock state belongs to (`new URL(API_BASE_URL).origin`). */
function apiOrigin(): string | null {
  try {
    const { origin } = new URL(API_BASE_URL);
    return origin === 'null' ? null : origin;
  } catch {
    // Not a URL: there is no origin to key the clock state by.
    return null;
  }
}

/**
 * The persisted clock state of this API origin. Not sealed: it holds no
 * personal or attendance data, it must be readable before (and without) the
 * data key, and the monitor treats it as untrusted anyway (`restore()` judges
 * and ranks it, and clears nothing).
 */
async function readClockState(): Promise<PersistedClockState> {
  const origin = apiOrigin();
  if (origin === null) return NO_CLOCK_STATE;
  try {
    const row = await offlineDb().meta.get(CLOCK_STATE_PREFIX + origin);
    return parseClockState(row?.value);
  } catch {
    // Unreadable: the monitor simply starts without it (and fails closed on a jump).
    return NO_CLOCK_STATE;
  }
}

async function persistClockState(): Promise<void> {
  const m = monitor;
  const origin = apiOrigin();
  if (m === null || origin === null || !isIndexedDbAvailable()) return;
  const value: PersistedClockState = { lastSeenWallMs: m.lastSeenWallMs(), calibration: m.calibration() };
  try {
    await offlineDb().meta.put({ key: CLOCK_STATE_PREFIX + origin, value });
  } catch (err) {
    // Best effort: the next tick or calibration writes it again.
    if (IS_DEV) console.warn('[offline-attendance] could not persist the clock state', errorName(err));
  }
}

function parseClockState(value: unknown): PersistedClockState {
  if (!isRecord(value)) return NO_CLOCK_STATE;
  const lastSeen = value.lastSeenWallMs;
  return {
    lastSeenWallMs: typeof lastSeen === 'number' && Number.isFinite(lastSeen) ? lastSeen : null,
    calibration: parseCalibration(value.calibration),
  };
}

function parseCalibration(value: unknown): ClockCalibration | null {
  if (!isRecord(value)) return null;
  const { offsetMs, uncertaintyMs, calibratedAtDeviceMs, calibratedAtServerIso } = value;
  if (
    typeof offsetMs !== 'number' ||
    typeof uncertaintyMs !== 'number' ||
    typeof calibratedAtDeviceMs !== 'number' ||
    typeof calibratedAtServerIso !== 'string'
  ) {
    return null;
  }
  return { offsetMs, uncertaintyMs, calibratedAtDeviceMs, calibratedAtServerIso };
}

/* ── Quiesce: the settlement window ────────────────────────────────────── */

function beginSettling(): void {
  if (settling !== null) return;
  let resolve: () => void = () => undefined;
  const promise = new Promise<void>((settle) => {
    resolve = settle;
  });
  const timer = setTimeout(endSettling, SETTLING_MAX_MS);
  settling = { promise, resolve, timer };
}

function endSettling(): void {
  const current = settling;
  if (current === null) return;
  settling = null;
  clearTimeout(current.timer);
  current.resolve();
}

/* ── Helpers ───────────────────────────────────────────────────────────── */

function sameScope(s: OfflineSession, scope: OfflineScope): boolean {
  return s.scope !== null && sameOfflineScope(s.scope, scope);
}

/** The newer of two snapshots by ERPat's `as_of` (a missing one never replaces a present one). */
function newerSnapshot(
  current: ConfirmedSnapshotRecord | null,
  candidate: ConfirmedSnapshotRecord | null,
): ConfirmedSnapshotRecord | null {
  if (candidate === null) return current;
  if (current === null) return candidate;
  return candidate.asOfMs >= current.asOfMs ? candidate : current;
}

/**
 * What a provisioning answer's capability means for readiness — the mapping
 * applyProvision() applies to a saved answer (C21: off and not ready reads
 * unsupported, re-asked slowly; off for any other reason, disabled).
 */
function backendOfCapability(capability: OfflineContextRecord['capability']): BackendInput {
  if (capability.offlineAttendance) return 'enabled';
  return capability.reason === 'not_ready' ? 'unsupported' : 'disabled';
}

/** A storage failure: a chain sealed under another key reads as a lost key, anything else as failed storage. */
function markStorageFailure(s: OfflineSession, err: unknown): void {
  if (!isStorageError(err)) {
    if (IS_DEV) console.warn('[offline-attendance] unexpected failure', errorName(err));
    return;
  }
  const cause: unknown = Reflect.get(err, 'cause');
  if (Reflect.get(err, 'kind') === 'corrupt' && cause instanceof SealedDataError && cause.kind === 'key_mismatch') {
    s.chainKeyMismatch = true;
  } else {
    s.storage = 'failed';
  }
  publish(s);
}

/** A repository `StorageError` (of `kind`, when given) — compared by shape, as across module copies. */
function isStorageError(err: unknown, kind?: string): err is Error {
  if (!(err instanceof Error) || err.name !== 'StorageError') return false;
  return kind === undefined || Reflect.get(err, 'kind') === kind;
}

async function deviceUnsettledCountOrNaN(): Promise<number> {
  try {
    // No database ⇒ nothing queued, and nothing created just to count (D6: a
    // session that does not capture must not open one on a fresh device).
    if (!(await offlineDbMayExist())) return 0;
    return await countUnsettled();
  } catch {
    // Unknown: readiness then assumes punches may be waiting.
    return Number.NaN;
  }
}

/** How long listing the device's databases may take before the answer counts as "may exist". */
const DB_LIST_TIMEOUT_MS = 2_000;

/**
 * Whether the offline database may exist on this device: `false` only when
 * IndexedDB lists its databases (`indexedDB.databases()`) and `syntry-offline`
 * is not among them. Without that API, or when the list fails or is slow, it
 * answers `true` (opening — and so creating — the database, as before). Listing
 * opens nothing, unlike `Dexie.exists()`, whose probe ends in a
 * `deleteDatabase` that could race the orchestrator creating the database.
 * Never rejects.
 */
async function offlineDbMayExist(): Promise<boolean> {
  try {
    const factory: unknown = Reflect.get(globalThis, 'indexedDB');
    const list: unknown = isRecord(factory) ? Reflect.get(factory, 'databases') : undefined;
    if (typeof list !== 'function') return true;
    const listed: unknown = await bounded(
      Promise.resolve<unknown>(Reflect.apply(list, factory, [])),
      DB_LIST_TIMEOUT_MS,
      null,
    );
    if (!Array.isArray(listed)) return true;
    return listed.some((info: unknown) => isRecord(info) && info.name === OFFLINE_DB_NAME);
  } catch {
    // The list itself failed (a restricted context): assume it may exist, as before.
    return true;
  }
}

async function scopeUnsettledOrNaN(scopeKey: string): Promise<number> {
  try {
    return await countUnsettled(scopeKey);
  } catch {
    // Unknown: readiness then assumes punches may be waiting.
    return Number.NaN;
  }
}

function bounded<T>(work: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((resolve) => {
    timer = setTimeout(() => resolve(fallback), ms);
  });
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer));
}

function isLockManager(value: unknown): value is LockManager {
  return typeof value === 'object' && value !== null && typeof Reflect.get(value, 'request') === 'function';
}

/** `navigator.locks` when the browser has it (happy-dom and older WebViews answer null). */
function webLocks(): LockManager | null {
  if (typeof navigator === 'undefined') return null;
  const locks: unknown = Reflect.get(navigator, 'locks');
  return isLockManager(locks) ? locks : null;
}

/** IndexedDB, WebCrypto and a monotonic clock: what storing punches securely needs. */
function supportedEnvironment(): boolean {
  const subtle: unknown = typeof crypto === 'undefined' ? undefined : Reflect.get(crypto, 'subtle');
  return isIndexedDbAvailable() && isRecord(subtle) && monotonicClockAvailable();
}

function nativePlatform(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    // No Capacitor bridge: the web.
    return false;
  }
}

function documentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function connectivityNow(): CapturedConnectivity {
  if (isOffline.value) return 'unreachable';
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'unreachable';
  return 'reachable';
}

function deviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // No Intl time zone: the outbox sends its "unknown" name.
    return '';
  }
}

/** The device-local calendar day of an instant (`''` for an instant that is not a number). */
function localDayKey(ms: number): string {
  return localDateKey(new Date(ms));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function errorName(err: unknown): string {
  return err instanceof Error ? err.name : typeof err;
}
